/*
 * cart-color-fallback.js
 *
 * Adds a color name + matching swatch dot to cart-drawer line items that
 * don't already have a Platmart swatch (i.e. the product isn't in the CSV)
 * and where Liquid couldn't extract a color from variant options or
 * "Product - Color" title split.
 *
 * Reads the color from the product title (e.g. "Khaki Alessia Blouse"
 * -> "Khaki", "Blue Polka Dot Claire Blouse" -> "Blue") using the same
 * dictionary as card-color-fallback.js.
 *
 * Runs after component-cart-platmart.js. Re-runs on cart updates via
 * MutationObserver and on the cart:updated event if other code dispatches it.
 *
 * Visual parity: uses the same .cart-platmart-swatch__dot and
 * .cart-platmart-swatch__label classes Platmart uses, so styles match.
 */
(function () {
  'use strict';

  // Color name -> CSS hex value. Same dictionary as card-color-fallback.js.
  var COLOR_HEX = {
    'black':       '#181714',
    'white':       '#FFFFFF',
    'ivory':       '#F4EDE0',
    'cream':       '#F4EDE0',
    'ecru':        '#E8DCC4',
    'beige':       '#D9C3A5',
    'nude':        '#D9C3A5',
    'sand':        '#C9B68C',
    'tan':         '#B8966B',
    'camel':       '#A0744B',
    'caramel':     '#A0744B',
    'brown':       '#5A3A22',
    'chocolate':   '#5A3A22',
    'grey':        '#8A8A8A',
    'gray':        '#8A8A8A',
    'silver':      '#BFC1C2',
    'red':         '#B83A3A',
    'burgundy':    '#6E1F1F',
    'wine':        '#6E1F1F',
    'pink':        '#F2B6C6',
    'fuchsia':     '#E91E8C',
    'coral':       '#F08070',
    'rose':        '#D88494',
    'orange':      '#E07B3A',
    'yellow':      '#E8C740',
    'mustard':     '#C9962E',
    'gold':        '#C9A14A',
    'green':       '#4F7A48',
    'olive':       '#3A5236',
    'mint':        '#A8D4A0',
    'teal':        '#3E8B82',
    'khaki':       '#8B8147',
    'blue':        '#3A5BA0',
    'navy':        '#1F2A5A',
    'denim':       '#4A6B8C',
    'turquoise':   '#3FB8C0',
    'purple':      '#7E4FA0',
    'lavender':    '#C8B5DC',
    'lilac':       '#C8B5DC',
    // two-word
    'sky blue':       '#A8C8E8',
    'light blue':     '#A8C8E8',
    'baby blue':      '#A8C8E8',
    'dark blue':      '#1F2A5A',
    'navy blue':      '#1F2A5A',
    'royal blue':     '#3A5BA0',
    'powder blue':    '#B8D4E8',
    'electric blue':  '#2A6FD8',
    'light pink':     '#F8D5DD',
    'baby pink':      '#F8D5DD',
    'hot pink':       '#E91E8C',
    'dusty pink':     '#D9A8B0',
    'dusty rose':     '#C49097',
    'rose gold':      '#D9A593',
    'light green':    '#A8D4A0',
    'dark green':     '#3A5236',
    'forest green':   '#2C4030',
    'sea green':      '#3E8B82',
    'mint green':     '#A8D4A0'
  };

  // Build longest-first lookup arrays so multi-word matches win.
  var TWO_WORD = [];
  var ONE_WORD = [];
  for (var name in COLOR_HEX) {
    if (!COLOR_HEX.hasOwnProperty(name)) continue;
    if (name.indexOf(' ') !== -1) TWO_WORD.push(name);
    else ONE_WORD.push(name);
  }

  // Try to extract a color name from a product title or item label.
  // Tries suffix (after " - ") first, then prefix, then any-word search.
  function extractColor(title) {
    if (!title) return null;
    var lower = title.toLowerCase().trim();

    // Suffix path: "Product Name - Color"
    var dashIdx = lower.lastIndexOf(' - ');
    if (dashIdx !== -1) {
      var suffix = lower.substring(dashIdx + 3).trim();
      if (COLOR_HEX[suffix] != null) return suffix;
      for (var ti = 0; ti < TWO_WORD.length; ti++) {
        if (suffix === TWO_WORD[ti] || suffix.indexOf(TWO_WORD[ti] + ' ') === 0) return TWO_WORD[ti];
      }
      for (var oi = 0; oi < ONE_WORD.length; oi++) {
        if (suffix === ONE_WORD[oi] || suffix.indexOf(ONE_WORD[oi] + ' ') === 0) return ONE_WORD[oi];
      }
    }

    // Prefix path: "Khaki Alessia Blouse"
    for (var i = 0; i < TWO_WORD.length; i++) {
      if (lower.indexOf(TWO_WORD[i] + ' ') === 0) return TWO_WORD[i];
    }
    for (var j = 0; j < ONE_WORD.length; j++) {
      if (lower.indexOf(ONE_WORD[j] + ' ') === 0) return ONE_WORD[j];
    }

    // Any-word path: "Blue Polka Dot Claire Blouse" -> "Blue"
    // Scan word-by-word; first single-word color match wins.
    var words = lower.split(/\s+/);
    for (var w = 0; w < words.length; w++) {
      // Check two-word match starting at this position
      if (w + 1 < words.length) {
        var pair = words[w] + ' ' + words[w + 1];
        if (COLOR_HEX[pair] != null) return pair;
      }
      if (COLOR_HEX[words[w]] != null) return words[w];
    }

    return null;
  }

  function titleCase(s) {
    return s.replace(/\b\w/g, function (m) { return m.toUpperCase(); });
  }

  // Find the cart line title corresponding to a color slot.
  // The DOM is: .cart-product > .cart-product-details > .cart-product-metas
  //              > .cart-product-metas__title-wrapper > .cart-product-metas__title-header
  //                  > .cart-product-metas__title (text)
  //              > .cart-product-metas__platmart (this is `slot`)
  function getTitleForSlot(slot) {
    var wrapper = slot.closest('.cart-product-metas__title-wrapper');
    if (!wrapper) return '';
    var titleEl = wrapper.querySelector('.cart-product-metas__title');
    return titleEl ? titleEl.textContent || '' : '';
  }

  function buildDot(hex) {
    var dot = document.createElement('span');
    dot.className = 'cart-platmart-swatch__dot';
    dot.style.width = '0.75rem';
    dot.style.height = '0.75rem';
    dot.style.backgroundColor = hex;
    if (hex.toLowerCase() === '#ffffff' || hex.toLowerCase() === '#fff') {
      dot.style.borderColor = '#d1d1d1';
    }
    return dot;
  }

  // For a single cart-line color slot: if it's empty (no Platmart hydrated
  // it AND no Liquid fallback wrote a label), fill it from the product title.
  function processSlot(slot) {
    if (!slot) return;
    if (slot.getAttribute('data-cart-color-fallback-done') === '1') return;

    // Skip if Platmart already populated this slot (it adds a swatch dot).
    if (slot.querySelector('.cart-platmart-swatch__dot')) {
      slot.setAttribute('data-cart-color-fallback-done', '1');
      return;
    }

    // If there's already a label (from Liquid fallback OR Platmart-without-dot),
    // we may STILL want to add a swatch dot if there isn't one. But only if we
    // can resolve the label text to a known color in our dictionary.
    var existingLabel = slot.querySelector('.cart-platmart-swatch__label');
    var colorName = null;

    if (existingLabel && existingLabel.textContent.trim()) {
      // A Liquid fallback wrote a label (e.g. "Khaki"). Try to match it to
      // our dictionary so we can add a swatch dot next to it.
      var labelText = existingLabel.textContent.trim().toLowerCase();
      if (COLOR_HEX[labelText] != null) {
        colorName = labelText;
      } else {
        // The label has multi-word text. Try to extract a color from it.
        colorName = extractColor(labelText);
      }
      // Already has visible color text -> mark done regardless of whether we
      // could resolve a hex, so we don't keep re-processing.
      if (!colorName) {
        slot.setAttribute('data-cart-color-fallback-done', '1');
        return;
      }
      // Prepend a dot before the existing label.
      var dot1 = buildDot(COLOR_HEX[colorName]);
      slot.insertBefore(dot1, existingLabel);
      slot.setAttribute('data-cart-color-fallback-done', '1');
      return;
    }

    // No label visible - extract from product title.
    var title = getTitleForSlot(slot);
    colorName = extractColor(title);
    if (!colorName) {
      slot.setAttribute('data-cart-color-fallback-done', '1');
      return;
    }

    var hex = COLOR_HEX[colorName];
    if (!hex) {
      slot.setAttribute('data-cart-color-fallback-done', '1');
      return;
    }

    // Build dot + label inside the slot.
    slot.innerHTML = '';
    slot.appendChild(buildDot(hex));
    var label = document.createElement('span');
    label.className = 'cart-platmart-swatch__label';
    label.textContent = titleCase(colorName);
    slot.appendChild(label);
    slot.setAttribute('data-cart-color-fallback-done', '1');
  }

  function processAll(root) {
    var scope = root || document;
    var slots = scope.querySelectorAll('[data-platmart-cart-line]');
    for (var i = 0; i < slots.length; i++) processSlot(slots[i]);
  }

  function init() {
    // Run after component-cart-platmart has had a chance to populate.
    setTimeout(function () { processAll(); }, 0);
    setTimeout(function () { processAll(); }, 300);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run when cart content is replaced (after add/remove/update).
  if ('MutationObserver' in window) {
    var observer = new MutationObserver(function (mutations) {
      var sawCartChange = false;
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          // Reset done flags on newly inserted slots so they get re-processed.
          if (node.matches && node.matches('[data-platmart-cart-line]')) {
            sawCartChange = true;
          } else if (node.querySelectorAll) {
            var inner = node.querySelectorAll('[data-platmart-cart-line]');
            if (inner.length) sawCartChange = true;
          }
        }
      }
      if (sawCartChange) {
        // Slight delay so Platmart processes new slots first.
        setTimeout(function () { processAll(); }, 50);
        setTimeout(function () { processAll(); }, 300);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // Public API for manual triggers.
  window.cartColorFallback = { refresh: function (r) { processAll(r); } };
})();
